import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { track } from '../tracking';
import '../styles/ActivityDrawer.css';
const ActivityDrawer = ({ isOpen, activityUrl, waitFor, onClose }) => {
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [showTimeoutError, setShowTimeoutError] = useState(false);
    const iframeRef = useRef(null);
    const waitTimeoutRef = useRef(null);
    const allowedMessageOrigin = 'https://playmoneymagic.com';
    // Handle animation trigger
    useEffect(() => {
        if (isOpen) {
            // Small delay to trigger CSS transition
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setShouldAnimate(true);
                });
            });
        }
        else {
            setShouldAnimate(false);
            setShowTimeoutError(false);
        }
    }, [isOpen]);
    // Handle waitFor logic
    useEffect(() => {
        if (!isOpen || !waitFor)
            return;
        console.log('[ActivityDrawer] waitFor enabled:', {
            message: waitFor.message,
            waitSeconds: waitFor.waitSeconds,
            activityUrl,
        });
        setShowTimeoutError(false);
        const timeout = setTimeout(() => {
            console.log('[ActivityDrawer] waitFor TIMEOUT - message not received within', waitFor.waitSeconds, 'seconds');
            setShowTimeoutError(true);
        }, waitFor.waitSeconds * 1000);
        waitTimeoutRef.current = timeout;
        return () => {
            if (waitTimeoutRef.current) {
                clearTimeout(waitTimeoutRef.current);
                waitTimeoutRef.current = null;
            }
        };
    }, [isOpen, waitFor, activityUrl]);
    // Handle escape key to close drawer
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleWindowMessage = (event) => {
            if (event.source !== iframeRef.current?.contentWindow)
                return;
            if (event.origin !== allowedMessageOrigin)
                return;
            // Check for the waitFor message if waitFor is defined
            if (waitFor && event.data && event.data.type === waitFor.message) {
                console.log('[ActivityDrawer] waitFor message RECEIVED:', waitFor.message);
                // Cancel timeout - game loaded successfully
                if (waitTimeoutRef.current) {
                    clearTimeout(waitTimeoutRef.current);
                    waitTimeoutRef.current = null;
                    console.log('[ActivityDrawer] waitFor timeout cleared - game loaded successfully');
                }
            }
            if (event.data && event.data.type === 'GAME_COMPLETED') {
                const payload = {
                    object: 'content',
                    action: 'engaged',
                    object_detail: 'playmoneymagic',
                    ui_object: 'message',
                    ui_action: 'received',
                    ui_object_detail: 'game_completed',
                };
                track(payload);
            }
        };
        window.addEventListener('message', handleWindowMessage);
        return () => {
            window.removeEventListener('message', handleWindowMessage);
        };
    }, [isOpen, waitFor]);
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: `activity-drawer ${shouldAnimate ? 'activity-drawer-open' : ''}`, children: [_jsx("div", { className: "activity-drawer-header", children: _jsxs("button", { className: "activity-drawer-back-btn", onClick: onClose, "aria-label": "Back to activities", children: [_jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M19 12H5M5 12L12 19M5 12L12 5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("span", { children: "Done playing? Go back and continue your challenge." })] }) }), _jsx("div", { className: "activity-drawer-content", children: showTimeoutError ? (_jsxs("div", { className: "activity-timeout-error", children: [_jsx("img", { src: "/assets/empty-wallet.png", alt: "Empty wallet illustration", className: "activity-timeout-error-image" }), _jsx("h2", { className: "activity-timeout-error-title", children: "Oops. Empty wallet?" }), _jsx("p", { className: "activity-timeout-error-message", children: "This game is currently offline. Don't worry! Head back to the main page and try another game to keep leveling up your financial skills!" }), _jsx("button", { className: "activity-timeout-error-button", onClick: onClose, children: "Back" })] })) : (_jsx("iframe", { ref: iframeRef, src: activityUrl, title: "Activity", sandbox: "allow-scripts allow-same-origin allow-forms allow-popups", className: "activity-drawer-iframe", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture", allowFullScreen: true })) })] }));
};
export default ActivityDrawer;
